/**
 * Pasarela de datos.
 *
 * POR QUE ESTE ARCHIVO EXISTE
 * ---------------------------
 * El sistema dejo de tener servidor propio: ahora las lecturas van directo a
 * PostgREST (filtradas por RLS) y las escrituras pasan por funciones RPC de la
 * base. Las veinte pantallas de la aplicacion, sin embargo, ya hablaban un
 * lenguaje de rutas HTTP y lo hacian bien.
 *
 * En vez de reescribir esas veinte pantallas —y arriesgar la caja, el fiado y
 * el corte por un cambio de plomeria— la traduccion vive AQUI, en un solo
 * lugar que se puede leer completo. Cada ruta tiene una linea que dice a que
 * llamada de Supabase corresponde.
 *
 * REGLA QUE NO SE ROMPE
 * ---------------------
 * Este archivo NO decide nada de negocio. No calcula precios, no valida
 * permisos y no arma movimientos de inventario. Traduce y mapea nombres
 * (`snake_case` de la base a `camelCase` del frontend). Toda la aritmetica de
 * dinero y todo el control de acceso viven en la base de datos, que es la
 * unica que no se puede saltar con la consola del navegador abierta.
 */

import {
  ERROR_MESSAGES,
  isErrorCode,
  normalizeBarcode,
  type ErrorCode,
} from '@abarrotes/shared';
import { functionsUrl, supabase } from './supabase.js';
import { recordApiFailure } from './error-log.js';
import { currentPushSubscription } from './push-device.js';

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly fields: Record<string, string> | undefined;
  readonly details: Record<string, unknown> | undefined;
  /**
   * El codigo CRUDO de PostgREST o Postgres, cuando lo hubo.
   *
   * `code` de arriba es el codigo del dominio, y para casi todo es el que
   * importa. Pero los tres fallos que dejan una pantalla vacia sin decir nada
   * —`PGRST201` (incrustacion ambigua), `42501` (permission denied) y `42703`
   * (columna que no existe)— se traducen los tres al mismo `INTERNAL_ERROR`, y
   * ahi se pierde justo el dato que los distingue.
   *
   * Sin esto, la bitacora de `lib/error-log.ts` seria decorativa: apuntaria
   * "INTERNAL_ERROR" en las tres causas del defecto que la hizo nacer.
   */
  readonly pgCode: string | undefined;

  constructor(
    code: ErrorCode,
    message: string,
    status: number,
    fields?: Record<string, string>,
    details?: Record<string, unknown>,
    pgCode?: string,
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.fields = fields;
    this.details = details;
    this.pgCode = pgCode;
  }

  /** True si el problema es que no hay sesion; la app manda al login. */
  get isAuthProblem(): boolean {
    return (
      this.code === 'UNAUTHENTICATED' ||
      this.code === 'SESSION_EXPIRED' ||
      this.code === 'ACCOUNT_DISABLED'
    );
  }
}

/**
 * Codigos que lanza la base y que no existen en el catalogo compartido de
 * errores, con su texto en español. Sin esto el cajero veria "Ocurrio un error"
 * cuando la causa real es perfectamente explicable.
 */
const DB_MESSAGES: Record<string, string> = {
  AUTH_REQUIRED: 'Tu sesion expiro. Vuelve a entrar.',
  FORBIDDEN: 'No tienes permiso para hacer eso.',
  STORE_NOT_FOUND: 'No se encontro la sucursal.',
  USER_NOT_FOUND: 'No se encontro el usuario.',
  USER_PROFILE_MISSING: 'Tu cuenta no tiene perfil. Avisa al dueño.',
  ORG_NAME_REQUIRED: 'Captura el nombre del negocio.',
  SUPPLIER_TAKEN: 'Ya existe un proveedor con ese nombre.',
  CATEGORY_TAKEN: 'Ya existe una categoria con ese nombre.',
  STORE_CODE_TAKEN: 'Ya existe una sucursal con esa clave.',
  STORE_PREFIX_EXHAUSTED: 'Se acabaron los prefijos internos de codigo de barras.',
  SCALE_PREFIX_CLASH: 'El prefijo de la bascula no puede ser igual al de los codigos internos.',
  LAST_OWNER: 'No puedes quitar al ultimo dueño del negocio.',
  BARCODE_SEQUENCE_EXHAUSTED: 'Se acabaron los consecutivos de codigo interno.',
  DOWN_PAYMENT_COVERS_TOTAL:
    'Lo que deja alcanza para pagar el ticket completo. Cobralo como efectivo en vez de ' +
    'fiarlo: un fiado con saldo cero solo ensucia la cartera.',
  // Aqui vivia `PRODUCT_HAS_HISTORY` —"ya tiene ventas: descontinualo en vez de
  // borrarlo"—. Se fue el 4 de septiembre de 2026 con el borrado con historia:
  // ninguna funcion de la base lo lanza ya (comprobado contra `pg_proc`), asi
  // que era un mensaje que no podia salir nunca. Su sucesor es `PRODUCT_DELETED`
  // y vive en el catalogo compartido, no aqui, porque esa es la unica rama de
  // `toApiError` que conserva el `using detail`.
  UNIT_LOCKED_BY_HISTORY:
    'Ese producto ya se movio, y su historia esta escrita en la unidad actual: ' +
    'cambiarla ahora volveria a leer el kardex en otra medida. Si esta mal capturado, ' +
    'descontinualo y dalo de alta otra vez.',
  // `UNIT_NOT_IN_PROFILE` y `UNIT_PROFILE_CONFLICT` NO viven aqui: estan en el
  // catalogo compartido, que es la unica rama de `toApiError` que conserva el
  // `using detail` de la base. Aqui llegarian convertidos en INTERNAL_ERROR y
  // sin el detalle -o sea, sin cuantos productos estorban, que es justo lo que
  // la pantalla necesita enseñar-.

  // --- entradas por aprobar ---
  INTAKE_NOT_PENDING: 'Esa entrada ya se habia revisado. Actualiza para ver como quedo.',

  // --- personal ---
  USER_HAS_HISTORY:
    'Esa persona ya vendio, cobro o capturo cosas en el sistema: borrar su cuenta se ' +
    'llevaria esa historia. Quitale el acceso en vez de borrarla.',
  // Errores de la Edge Function que administra el personal. Son fallos de la
  // plataforma de identidad, no del negocio, asi que el texto dice que hacer.
  AUTH_CREATE_FAILED: 'No se pudo crear la cuenta. Revisa el usuario y vuelve a intentar.',
  AUTH_UPDATE_FAILED: 'No se pudo cambiar la contrasena. Vuelve a intentar.',
  MEMBERSHIP_FAILED: 'La cuenta se creo pero no se le pudo dar el puesto. Vuelve a intentar.',
  DELETE_FAILED: 'No se pudo borrar la cuenta.',
  CONFIG_ERROR: 'Falta configuracion en el servidor. Avisale a quien lo instalo.',
};

/** Traduce el error de Supabase al error del dominio, con su texto. */
function toApiError(raw: unknown, status = 400): ApiError {
  const err = raw as { message?: string; details?: string; hint?: string; code?: string } | null;
  const message = (err?.message ?? '').trim();

  // Las RPC lanzan el codigo pelado (`raise exception 'FORBIDDEN'`), asi que el
  // mensaje ES el codigo.
  const token = message.split(/[\s:]/)[0] ?? '';

  if (isErrorCode(token)) {
    let details: Record<string, unknown> | undefined;
    // `using detail = ...` viaja como JSON; trae saldo, limite y disponible.
    if (err?.details) {
      try {
        details = JSON.parse(err.details) as Record<string, unknown>;
      } catch {
        details = undefined;
      }
    }
    return new ApiError(token, ERROR_MESSAGES[token], status, undefined, details);
  }

  if (token in DB_MESSAGES) {
    const isAuth = token === 'AUTH_REQUIRED';
    return new ApiError(
      isAuth ? 'UNAUTHENTICATED' : 'INTERNAL_ERROR',
      DB_MESSAGES[token] ?? ERROR_MESSAGES.INTERNAL_ERROR,
      isAuth ? 401 : token === 'FORBIDDEN' ? 403 : status,
    );
  }

  /*
   * Aqui caen los fallos que NO son del dominio, y son los peores de
   * diagnosticar: `PGRST201`, `42501` y `42703` se ven los tres igual en
   * pantalla —una lista vacia— y son cosas distintas. El texto para la persona
   * sigue siendo el mismo; el codigo crudo se conserva aparte para la bitacora.
   */
  return new ApiError(
    'INTERNAL_ERROR',
    message || ERROR_MESSAGES.INTERNAL_ERROR,
    status,
    undefined,
    undefined,
    typeof err?.code === 'string' && err.code !== '' ? err.code : undefined,
  );
}

/**
 * Llama la Edge Function que administra el personal.
 *
 * Es la unica salida del frontend fuera de PostgREST, y existe porque alta,
 * cambio de contrasena y borrado tocan `auth.users`: eso necesita la llave de
 * servicio, que jamas puede viajar al navegador. Se manda el JWT de la sesion
 * para que alla adentro el permiso se valide en Postgres y la auditoria quede
 * con el nombre de quien lo pidio.
 *
 * El error viaja como `{ error: { code } }` y se traduce con el MISMO catalogo
 * que los errores de la base, para que no haya dos listas de textos.
 */
async function staffFunction<T>(payload: Record<string, unknown>): Promise<T> {
  const { data: session } = await supabase.auth.getSession();
  const token = session.session?.access_token;
  if (!token) throw new ApiError('UNAUTHENTICATED', ERROR_MESSAGES.UNAUTHENTICATED, 401);

  const response = await fetch(`${functionsUrl}/invite-staff`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  const body = (await response.json().catch(() => ({}))) as Row;
  if (!response.ok) {
    const err = (body.error ?? {}) as Row;
    throw toApiError({ message: String(err.code ?? 'INTERNAL_ERROR') }, response.status);
  }
  return body as T;
}

/**
 * Al cerrar sesion, este aparato deja de recibir los avisos de quien se va.
 *
 * Va ANTES de `signOut`, que es cuando todavia hay sesion con que decirle a la
 * base de quien es la suscripcion. Sin esto, el telefono prestado del mostrador
 * seguiria enseñando en su pantalla bloqueada el reporte del dia de la dueña
 * despues de que ella salio y entro el empleado.
 *
 * Nada de esto puede impedir salir: sin red, la suscripcion se suelta en el
 * aparato y la base la borra sola la primera vez que el servicio de push le
 * diga que ya no existe (`private.prune_push_subscriptions`).
 */
async function forgetThisDevice(): Promise<void> {
  const subscription = await currentPushSubscription({ wait: false }).catch(() => null);
  if (!subscription) return;
  try {
    await rpc('delete_push_subscription', { p_endpoint: subscription.endpoint });
  } catch {
    // Ver arriba: no se detiene la salida por esto.
  }
  await subscription.unsubscribe().catch(() => false);
}

/** Llama una funcion de la base y devuelve su resultado ya tipado. */
async function rpc<T>(name: string, args: Record<string, unknown> = {}): Promise<T> {
  const { data, error } = await supabase.rpc(name, args);
  if (error) throw toApiError(error);
  return data as T;
}

/** Lectura por PostgREST. Las filas que devuelve ya vienen filtradas por RLS. */
async function unwrap<T>(promise: PromiseLike<{ data: T | null; error: unknown }>): Promise<T> {
  const { data, error } = await promise;
  if (error) throw toApiError(error);
  return (data ?? []) as T;
}

// ---------------------------------------------------------------------------
// Mapeo de nombres: la base habla snake_case, el frontend camelCase.
// ---------------------------------------------------------------------------

type Row = Record<string, any>;

/*
 * Listas de columnas explicitas. Aqui NO hay un solo `select('*')`, y no es
 * estilo: es la unica forma de que estas lecturas sigan funcionando.
 *
 * El costo de la mercancia es de gerente en adelante (`inventory:view_cost`).
 * Filtrarlo en el navegador no contaria —el dato ya habria viajado—, asi que
 * quien lo impide es Postgres: `authenticated` ya no tiene privilegio de SELECT
 * sobre `store_products.cost_cents`, `sales.cost_cents`,
 * `sale_items.unit_cost_cents` ni `inventory_movements.unit_cost_cents`.
 *
 * Y ahi esta la trampa: un `*` sobre estas tablas no devolveria menos columnas,
 * FALLARIA. Postgres expande el asterisco a todas las columnas de la tabla y
 * comprueba el privilegio de cada una, asi que la lectura entera se rechaza con
 * "permission denied" y la pantalla se queda vacia.
 *
 * Si algun dia se agrega una columna a estas tablas hay que agregarla en DOS
 * lugares: a su lista de aqui y a su GRANT en la migracion. Si solo se agrega a
 * la lista de aqui, la lectura empieza a fallar entera.
 */
const SALE_COLUMNS =
  'id, store_id, user_id, cash_session_id, folio, subtotal_cents, discount_cents, ' +
  'total_cents, payment_method, cash_received_cents, change_cents, status, note, ' +
  'customer_id, business_date, created_at, cancelled_at';

/*
 * El nombre del cajero se pide POR SU LLAVE FORANEA, y eso no es verbosidad.
 *
 * `sales` tiene DOS caminos hacia `users`: quien cobro (`user_id`) y quien
 * cancelo (`cancelled_by`). Ante dos caminos PostgREST no elige uno: contesta
 * `300 PGRST201` ("could not embed because more than one relationship was
 * found") y la lectura ENTERA falla. No devuelve la venta sin el nombre del
 * cajero — no devuelve NADA.
 *
 * Asi estuvo el historial de ventas: la caja registraba perfecto —folio, stock,
 * kardex y corte cuadrando— y la pantalla de Ventas contestaba "Sin ventas
 * registradas", que es la forma mas cara de fallar que tiene un punto de venta:
 * el dueño no ve un error, ve que el dinero del dia no existe.
 *
 * La regla general: cualquier tabla con dos llaves foraneas al mismo destino
 * SOLO se puede incrustar nombrando la llave. Hoy queda UN par en todo el
 * esquema —`sales`->`users`— y `npm run audit:reads` lo prueba contra PostgREST
 * de verdad en cada revision. Eran tres hasta el 19 de agosto de 2026: los otros
 * dos eran `transfers`->`users` y `transfers`->`stores`, y se fueron con los
 * traspasos.
 *
 * Va con alias (`cashier:`) y no a secas por una segunda razon: sin alias, el
 * nombre de la llave en el JSON lo decide PostgREST —usa el de la tabla— y el
 * mapeo de aqui abajo dependeria de esa regla. Con alias lo decidimos nosotros,
 * y de paso el JSON dice quien es: el cajero que cobro, no el que cancelo.
 */
const SALE_USER = 'cashier:users!sales_user_id_fkey(full_name)';

const SALE_ITEM_COLUMNS =
  'id, sale_id, product_id, name_snapshot, unit_snapshot, qty_milli, ' +
  'unit_price_cents, line_total_cents, is_wholesale, is_manual_price';

/**
 * Cuantos productos acompañan a cada venta en el HISTORIAL.
 *
 * Tres es techo, no promesa: la celda recorta lo que no le quepa, y a 800 px
 * —el ancho mas angosto en que la tabla se enseña, medido— de esos tres nombres
 * se ven 78 px de los 190 que miden. Subirlo no enseñaria mas; solo cargaria
 * texto para tirarlo. Lo que sostiene la cuenta es el "+N mas", que va fuera
 * del recorte a proposito.
 */
const SALE_PREVIEW_ITEMS = 3;

const MOVEMENT_COLUMNS =
  'id, store_id, product_id, type, qty_delta_milli, balance_after_milli, reason, ' +
  'ref_type, ref_id, user_id, business_date, created_at';

const PURCHASE_COLUMNS =
  'id, folio, supplier_id, reference, total_cents, note, business_date, created_at, ' +
  'status, cancelled_at, cancel_reason';

/**
 * Lo que el HISTORIAL de entradas incrusta ademas de sus columnas.
 *
 * Va en una constante, y como cadenas unidas con `+` en vez de una plantilla,
 * por dos exigencias del analizador de `npm run audit:reads` que conviene
 * conocer antes de "limpiar" esto:
 *
 *   1. Busca el `.select(` dentro de los 240 caracteres que siguen al `.from(`
 *      y captura ahi UN literal o UNA constante en mayusculas. Esta lista no
 *      cabe en esa ventana, asi que escrita en el sitio quedaria SIN AUDITAR —
 *      y una lectura sin auditar es justo la que se rompe sin que nadie se
 *      entere.
 *   2. Solo sabe resolver constantes que sean cadenas con comillas simples
 *      unidas por `+`. Una plantilla no la resuelve.
 *
 * Las dos llaves a `users` van NOMBRADAS: `purchases` tiene dos caminos hacia
 * esa tabla —quien recibio (`user_id`) y quien cancelo (`cancelled_by`)— y ante
 * dos caminos PostgREST contesta `300 PGRST201` sin devolver una sola fila. Es
 * el defecto que dejo el historial de ventas en blanco del 10 al 12 de agosto
 * de 2026, y el control positivo de esa auditoria existe para cazarlo.
 */
const PURCHASE_HISTORY_EMBEDS =
  'suppliers(name), ' +
  'buyer:users!purchases_user_id_fkey(full_name), ' +
  'canceller:users!purchases_cancelled_by_fkey(full_name), ' +
  'purchase_items(product_id, qty_milli, unit_cost_cents, line_total_cents, ' +
  'products(name, sku, unit, is_weighed))';

function mapSale(r: Row): Row {
  return {
    id: r.id,
    storeId: r.store_id,
    userId: r.user_id,
    // `cashier`, no `users`: lo nombra el alias de `SALE_USER`. Ver su nota.
    userName: r.cashier?.full_name ?? '',
    cashSessionId: r.cash_session_id,
    folio: r.folio,
    subtotalCents: r.subtotal_cents,
    discountCents: r.discount_cents,
    totalCents: r.total_cents,
    // Cero, no ausente: es la misma convencion que usa `catalog_json` en la
    // base para el costo que no se puede ver, y la interfaz ya sabe tratarla.
    // Esta lectura va por PostgREST, que no puede decidir por permiso; quien si
    // puede es `private.sale_json`, y es de donde sale el ticket con su costo
    // para quien tiene `inventory:view_cost`.
    costCents: 0,
    paymentMethod: r.payment_method,
    cashReceivedCents: r.cash_received_cents,
    changeCents: r.change_cents,
    status: r.status,
    note: r.note,
    customerId: r.customer_id,
    customerName: r.customers?.name ?? null,
    // Lo rellena quien lo necesita: vive en el libro de credito, no en la
    // venta. El historial lo pide aparte; la lista no lo muestra.
    downPaymentCents: 0,
    businessDate: r.business_date,
    createdAt: r.created_at,
    cancelledAt: r.cancelled_at,
    // El historial los rellena con su propia consulta; el detalle no los usa
    // porque ya trae los renglones completos. Ver `SALE_ITEM_COUNT_NOTE`.
    itemCount: 0,
    items: [],
  };
}

function mapSaleItem(r: Row): Row {
  return {
    id: r.id,
    saleId: r.sale_id,
    productId: r.product_id,
    nameSnapshot: r.name_snapshot,
    unitSnapshot: r.unit_snapshot,
    qtyMilli: r.qty_milli,
    unitPriceCents: r.unit_price_cents,
    // Ver la nota de `mapSale`: cero es "sin costo a la vista", no "costo cero".
    unitCostCents: 0,
    lineTotalCents: r.line_total_cents,
    isWholesale: r.is_wholesale ?? false,
    isManualPrice: r.is_manual_price ?? false,
  };
}

/**
 * Termino de busqueda de clientes, normalizado como lo guarda la base.
 *
 * `customers.search_text` se escribe con `private.normalize_search`: minusculas,
 * SIN acentos y con los espacios colapsados. Si el termino no se normaliza
 * igual, la busqueda no falla —contesta que no hay nadie—, que es la peor forma
 * de fallar aqui: quien teclea "José" no encuentra al "Jose" que ya existe, y
 * en la caja eso termina en una segunda ficha para la misma persona, con su
 * deuda partida en dos. Solo puede agregar coincidencias: el lado guardado ya
 * viene sin acentos, asi que un termino sin acentos seguia funcionando igual.
 */
function normalizeSearch(value: string): string {
  return (
    value
      .toLowerCase()
      // NFD parte cada letra acentuada en letra + marca; `\p{M}` se lleva las
      // marcas y deja la letra, que es lo mismo que hace `unaccent` en la base.
      .normalize('NFD')
      .replace(/\p{M}/gu, '')
      .replace(/\s+/g, ' ')
      .trim()
  );
}

function mapCustomer(r: Row): Row {
  return {
    id: r.id,
    name: r.name,
    phone: r.phone,
    notes: r.notes,
    creditLimitCents: r.credit_limit_cents,
    creditDays: r.credit_days,
    balanceCents: r.balance_cents,
    isActive: r.is_active,
    createdAt: r.created_at,
    lastMovementAt: r.last_movement_at ?? null,
  };
}

function mapCreditMovement(r: Row): Row {
  return {
    id: r.id,
    customerId: r.customer_id,
    storeId: r.store_id,
    type: r.type,
    amountCents: r.amount_cents,
    balanceAfterCents: r.balance_after_cents,
    dueDate: r.due_date,
    saleId: r.sale_id,
    userId: r.user_id,
    userName: r.users?.full_name ?? '',
    note: r.note,
    businessDate: r.business_date,
    createdAt: r.created_at,
  };
}

function mapCashSession(r: Row): Row {
  return {
    id: r.id,
    storeId: r.store_id,
    userId: r.user_id,
    userName: r.users?.full_name ?? '',
    openingCents: r.opening_cents,
    expectedCents: r.expected_cents,
    countedCents: r.counted_cents,
    differenceCents: r.difference_cents,
    note: r.note,
    businessDate: r.business_date,
    openedAt: r.opened_at,
    closedAt: r.closed_at,
  };
}

function mapCashMovement(r: Row): Row {
  return {
    id: r.id,
    cashSessionId: r.cash_session_id,
    storeId: r.store_id,
    userId: r.user_id,
    type: r.type,
    amountCents: r.amount_cents,
    reason: r.reason,
    createdAt: r.created_at,
  };
}

function mapExpense(r: Row): Row {
  return {
    id: r.id,
    storeId: r.store_id,
    userId: r.user_id,
    userName: r.users?.full_name ?? '',
    category: r.category,
    description: r.description ?? null,
    amountCents: r.amount_cents,
    paidFromCashDrawer: r.paid_from_cash_drawer,
    recurrence: r.recurrence,
    businessDate: r.business_date,
    createdAt: r.created_at,
  };
}

function mapMovement(r: Row): Row {
  return {
    id: r.id,
    storeId: r.store_id,
    productId: r.product_id,
    productName: r.products?.name ?? '',
    unit: r.products?.unit ?? 'pz',
    isWeighed: r.products?.is_weighed ?? false,
    type: r.type,
    qtyDeltaMilli: r.qty_delta_milli,
    balanceAfterMilli: r.balance_after_milli,
    // Ver la nota de `mapSale`: cero es "sin costo a la vista", no "costo cero".
    unitCostCents: 0,
    reason: r.reason,
    refType: r.ref_type,
    refId: r.ref_id,
    userId: r.user_id,
    userName: r.users?.full_name ?? null,
    businessDate: r.business_date,
    createdAt: r.created_at,
  };
}

/** Sesion del usuario con sus sucursales y permisos. */
async function loadSession(): Promise<Row> {
  const { data: auth } = await supabase.auth.getUser();
  const authUser = auth?.user;
  if (!authUser) throw new ApiError('UNAUTHENTICATED', ERROR_MESSAGES.UNAUTHENTICATED, 401);

  const profile = await unwrap<Row[]>(
    supabase.from('users').select('id, full_name, username, is_active, created_at').eq('id', authUser.id).limit(1),
  );

  const memberships = await unwrap<Row[]>(
    supabase
      .from('user_store_roles')
      .select('role, store_id, stores!inner(id, name, code, is_active, unit_profile)')
      .eq('user_id', authUser.id),
  );

  const active = memberships.filter((m) => m.stores?.is_active);
  const permissions = await unwrap<Row[]>(supabase.from('role_permissions').select('role, permission'));

  const byRole = new Map<string, string[]>();
  for (const p of permissions) {
    const list = byRole.get(p.role) ?? [];
    list.push(p.permission);
    byRole.set(p.role, list);
  }

  const permissionsByStore: Record<string, string[]> = {};
  for (const m of active) permissionsByStore[String(m.store_id)] = byRole.get(m.role) ?? [];

  const p = profile[0];
  return {
    user: {
      id: authUser.id,
      username: p?.username ?? null,
      fullName: p?.full_name ?? authUser.email ?? 'Usuario',
      isActive: p?.is_active ?? true,
      createdAt: p?.created_at ?? Date.now(),
      stores: active
        .map((m) => ({
          storeId: m.store_id,
          storeName: m.stores?.name ?? '',
          storeCode: m.stores?.code ?? '',
          role: m.role,
          // Sin respuesta se asume tienda de abarrotes, que es lo que era todo
          // hasta hoy: una sucursal vieja nunca queda con menos unidades de las
          // que ya usaba por culpa de una lectura a medias.
          unitProfile: m.stores?.unit_profile === 'bulk' ? 'bulk' : 'retail',
        }))
        .sort((a, b) => a.storeName.localeCompare(b.storeName)),
    },
    permissionsByStore,
  };
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  query?: Record<string, string | number | boolean | undefined>;
}

/**
 * Marca servida como archivo estatico.
 *
 * El despliegue de demostracion contesta `/api/branding` con un JSON fijo
 * (`netlify.toml`). Es la unica forma de mostrar el nombre real del negocio
 * ANTES de iniciar sesion, porque sin sesion la base no devuelve nada.
 *
 * Donde ese archivo no existe —la tienda real, o `npm run dev`— la ruta cae
 * en el index de la PWA y el JSON no parsea. Se devuelve null sin ruido: es
 * una ausencia esperada, no una falla.
 */
async function readStaticBranding(): Promise<
  { name: string; shortName: string; tagline: string } | null
> {
  try {
    const res = await fetch('/api/branding', { headers: { accept: 'application/json' } });
    if (!res.ok) return null;
    const raw: unknown = await res.json();
    if (typeof raw !== 'object' || raw === null) return null;
    const j = raw as Record<string, unknown>;
    if (typeof j['name'] !== 'string' || j['name'] === '') return null;
    return {
      name: j['name'],
      shortName: typeof j['shortName'] === 'string' ? j['shortName'] : j['name'],
      tagline: typeof j['tagline'] === 'string' ? j['tagline'] : '',
    };
  } catch {
    return null;
  }
}

/**
 * Punto unico de datos.
 *
 * Conserva la firma de rutas que ya usaban las pantallas; por dentro cada una
 * apunta a su lectura o a su RPC.
 *
 * ESTA ENVOLTURA SOLO APUNTA LOS FALLOS Y LOS VUELVE A LANZAR.
 * ----------------------------------------------------------
 * No cambia el comportamiento de una sola pantalla: lo que fallaba sigue
 * fallando igual y con el mismo error. Lo que agrega es que quede constancia.
 *
 * Existe porque el `300 PGRST201` que dejo el historial de ventas en blanco vivio
 * tres dias sin que nadie lo viera. Siendo este el unico punto por donde pasan
 * todas las peticiones, es tambien el unico donde se puede apuntar una vez y
 * valer para las veinte pantallas. Ver `lib/error-log.ts` para lo que se guarda
 * y —mas importante— lo que NO se guarda.
 */
export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  try {
    return await dispatch<T>(path, options);
  } catch (err) {
    recordApiFailure(path, options.method ?? 'GET', err, Object.keys(options.query ?? {}));
    throw err;
  }
}

/** El despachador de verdad. Se llama solo desde `api`, que apunta sus fallos. */
async function dispatch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const q = options.query ?? {};
  const body = (options.body ?? {}) as Row;
  const seg = path.split('?')[0]!.split('/').filter(Boolean); // ['api','stores','1','sales']
  const out = <R,>(value: R): T => value as unknown as T;

  // ---------------------------------------------------------------- sesion
  if (seg[1] === 'auth') {
    if (seg[2] === 'me') return out(await loadSession());

    if (seg[2] === 'login') {
      const identifier = String(body.username ?? body.email ?? '').trim();

      /*
       * QUIEN ESTA EN EL MOSTRADOR TECLEA SU NOMBRE DE USUARIO, NO UN CORREO.
       *
       * Supabase Auth autentica por correo, y a quien no tiene uno se le
       * sintetiza `usuario@<slug>.abarrotes.local` al darlo de alta. Nadie se
       * sabe esa direccion: Rosa quedo como `rosa` con correo
       * `rosa@abarrotes-la-esquina.abarrotes.local` y no podia entrar de
       * ninguna forma.
       *
       * `resolve_login` es la unica RPC nueva que alcanza `anon`, por fuerza:
       * traducir el usuario a su correo ocurre ANTES de que exista sesion.
       * Devuelve solo direcciones sinteticas, asi que un correo personal no
       * sale nunca. Si no resuelve, se intenta con lo que se tecleo tal cual y
       * el error que llega es el de credenciales de siempre.
       */
      let email = identifier;
      if (identifier && !identifier.includes('@')) {
        try {
          const resolved = await rpc<{ email: string | null }>('resolve_login', {
            p_username: identifier,
          });
          if (resolved?.email) email = resolved.email;
        } catch {
          // Que la traduccion falle no puede impedir entrar a quien SI escribio
          // su correo completo.
        }
      }

      const { error } = await supabase.auth.signInWithPassword({
        email,
        password: String(body.password ?? ''),
      });
      if (error) {
        throw new ApiError(
          'INVALID_CREDENTIALS',
          ERROR_MESSAGES.INVALID_CREDENTIALS,
          401,
        );
      }
      return out(await loadSession());
    }

    if (seg[2] === 'logout') {
      /*
       * SALIR ES DE ESTE APARATO, NO DE LA CUENTA.
       *
       * `signOut()` sin argumentos es GLOBAL: borra TODAS las sesiones de esa
       * persona en TODOS los aparatos. Con una cuenta compartida entre la
       * all-in-one del mostrador y el celular —que es como se usa de verdad—,
       * tocar "Salir" en el telefono tira a quien esta cobrando.
       *
       * No es hipotetico. Medido en produccion el 25 de agosto de 2026: un
       * `POST /logout` a las 13:52:56 y, CINCO SEGUNDOS despues, la PC del
       * mostrador contestando `400 Invalid Refresh Token: Refresh Token Not
       * Found` y de vuelta a la pantalla de acceso, a media jornada y con la
       * caja abierta. La dueña lo vivio como "me cerro la cuenta y no me deja
       * entrar", que es exactamente lo que se ve desde su lado.
       *
       * Con 'local' se cierra la sesion de ESTE navegador y ninguna otra. Es lo
       * que el boton dice que hace, y es la misma regla que `session.tsx` ya
       * defiende al reintentar `/me`: nadie sale del sistema por algo que no
       * tiene que ver con su sesion.
       *
       * `scripts/seed.mjs` ya lo sabia y pasaba el alcance; la caja no. Si
       * alguna vez hace falta cerrar en todos lados —una contraseña filtrada—
       * eso es una operacion aparte y con su nombre, no el boton de todos los
       * dias.
       */
      await forgetThisDevice();
      await supabase.auth.signOut({ scope: 'local' });
      return out({ ok: true });
    }
  }

  // ----------------------------------------------------- avisos al telefono
  // No cuelgan de una sucursal: el aparato es de la PERSONA, y a que avisos
  // tiene derecho lo decide la base aviso por aviso (migracion 20261018000000).
  if (seg[1] === 'push') {
    if (seg[2] === 'key' && !seg[3] && method === 'GET') {
      return out(await rpc('get_push_public_key'));
    }
    if (seg[2] === 'subscription' && !seg[3] && method === 'POST') {
      return out(
        await rpc('save_push_subscription', {
          p_endpoint: body.endpoint,
          p_p256dh: body.p256dh,
          p_auth: body.auth,
          p_device_label: body.deviceLabel ?? null,
        }),
      );
    }
    if (seg[2] === 'subscription' && !seg[3] && method === 'DELETE') {
      return out(await rpc('delete_push_subscription', { p_endpoint: body.endpoint }));
    }
    if (seg[2] === 'devices' && !seg[3] && method === 'GET') {
      return out(await rpc('get_push_devices', { p_current_endpoint: q.endpoint ?? null }));
    }
    if (seg[2] === 'test' && !seg[3] && method === 'POST') {
      return out(await rpc('send_test_push'));
    }
  }

  // --------------------------------------------------------------- marca
  // Con sesion, el nombre del negocio sale de su organizacion.
  //
  // Sin sesion no hay de donde: `anon` no lee una sola fila, asi que la
  // consulta vuelve vacia. Y ese es justo el momento en que la marca mas se
  // ve —la pantalla de acceso y el recorrido publico, que es lo que recibe
  // quien abre el enlace compartido—. Por eso se cae al archivo estatico
  // antes que al `.env`.
  if (seg[1] === 'branding') {
    const rows = await unwrap<Row[]>(supabase.from('organizations').select('name').limit(1));
    const first = rows[0];
    const orgName = first && typeof first.name === 'string' ? first.name : undefined;

    if (orgName) {
      return out({
        name: orgName,
        shortName: import.meta.env.VITE_BRAND_SHORT ?? orgName,
        tagline: import.meta.env.VITE_BRAND_TAGLINE ?? '',
      });
    }

    const published = await readStaticBranding();
    if (published) return out(published);

    const fallback = import.meta.env.VITE_BRAND_NAME ?? 'Farmacia San Carlos';
    return out({
      name: fallback,
      shortName: import.meta.env.VITE_BRAND_SHORT ?? fallback,
      tagline: import.meta.env.VITE_BRAND_TAGLINE ?? '',
    });
  }

  if (seg[1] === 'categories') {
    /*
      Corregir y quitar categorias. Solo tenian alta, asi que un nombre mal
      tecleado se quedaba asi. Borrar solo procede si no la usa ningun producto
      —si la usa, la base contesta `CATEGORY_IN_USE` diciendo cuantos—, porque
      `products.category_id` apunta aqui y quedarian mirando a una fila que ya
      no existe.
    */
    if (seg[2] && method === 'PATCH') {
      return out({
        category: await rpc('update_category', {
          p_store_id: Number(q.storeId ?? 0),
          p_category_id: Number(seg[2]),
          p_name: body.name,
          p_color: body.color ?? null,
          p_sort_order: body.sortOrder ?? null,
        }),
      });
    }
    if (seg[2] && method === 'DELETE') {
      return out(
        await rpc('delete_category', {
          p_store_id: Number(q.storeId ?? 0),
          p_category_id: Number(seg[2]),
        }),
      );
    }
    const rows = await unwrap<Row[]>(
      supabase.from('categories').select('id, name, color, sort_order').order('sort_order').order('name'),
    );
    return out({
      categories: rows.map((r) => ({ id: r.id, name: r.name, color: r.color, sortOrder: r.sort_order })),
    });
  }

  // ------------------------------------------------------------ sucursal
  if (seg[1] === 'stores' && seg[2]) {
    const storeId = Number(seg[2]);
    const s3 = seg[3];
    const s4 = seg[4];
    const s5 = seg[5];

    // --- panel y reportes -------------------------------------------------
    // --- contacto de la sucursal, para el ticket ---------------------------
    // Lectura directa: `sel_stores` deja ver su fila a cualquier miembro, y el
    // cajero que imprime no tiene `store:manage` para `get_organization`.
    if (s3 === 'contact' && !s4) {
      const rows = await unwrap<Row[]>(
        supabase.from('stores').select('address, phone').eq('id', storeId).limit(1),
      );
      const r = rows[0] ?? {};
      const text = (v: unknown): string | null =>
        typeof v === 'string' && v.trim() !== '' ? v.trim() : null;
      return out({ address: text(r.address), phone: text(r.phone) });
    }

    if (s3 === 'dashboard' && !s4) {
      return out({ dashboard: await rpc('get_dashboard', { p_store_id: storeId }) });
    }
    /*
     * El desglose de una cifra del Panel. `!s4` arriba no es rigor de mas: una
     * ruta que no exige el final del camino se traga lo que no es suyo, y el
     * alta de producto ya lo vivio (§13).
     *
     * `section` es el nombre que usa LA PANTALLA, no `p_section`: copiar el del
     * argumento SQL deja el valor en undefined sin que TypeScript diga nada.
     */
    if (s3 === 'dashboard' && s4 === 'detail' && !s5) {
      return out({
        detail: await rpc('get_dashboard_detail', {
          p_store_id: storeId,
          p_section: String(q.section ?? ''),
        }),
      });
    }
    if (s3 === 'reports' && s4 === 'daily') {
      return out(await rpc('get_daily_report', { p_store_id: storeId, p_date: String(q.date ?? '') }));
    }
    if (s3 === 'reports' && s4 === 'range') {
      return out(
        await rpc('get_report_range', {
          p_store_id: storeId,
          p_from: String(q.from ?? ''),
          p_to: String(q.to ?? ''),
        }),
      );
    }
    if (s3 === 'intelligence') {
      return out({ report: await rpc('get_intelligence', { p_store_id: storeId }), cached: false });
    }

    // --- campanita ---------------------------------------------------------
    /*
     * Se lee por RPC y no por PostgREST aunque la RLS de `notifications` ya
     * bastaria: el "sin leer" es una anti-union contra `notification_reads`, y
     * armarla aqui serian dos consultas y un cruce en el navegador para un dato
     * —la burbuja— que se pide en las ocho pantallas.
     *
     * `limit` es el nombre que usa la PANTALLA, no `p_limit` (§13): copiar el
     * del argumento SQL deja el valor en undefined sin que TypeScript diga nada.
     */
    if (s3 === 'notifications' && s4 === 'read' && method === 'POST') {
      return out(
        await rpc('mark_notifications_read', {
          p_store_id: storeId,
          // Nulo = todos los visibles, que es el boton "marcar todo".
          p_ids: body.ids ?? null,
        }),
      );
    }
    if (s3 === 'notifications') {
      return out(await rpc('get_notifications', { p_store_id: storeId, p_limit: Number(q.limit ?? 30) }));
    }

    // --- ventas -----------------------------------------------------------
    if (s3 === 'sales' && !s4 && method === 'POST') {
      return out(
        await rpc('create_sale', {
          p_store_id: storeId,
          p_items: (body.items as Row[]).map((i) => ({
            product_id: i.productId,
            qty_milli: i.qtyMilli,
            override_price_cents: i.overridePriceCents ?? null,
            /*
             * MAYOREO: viaja la BANDERA, jamas el importe.
             *
             * El precio lo lee `create_sale` de `wholesale_price_cents` y el
             * renglon lo sigue calculando `private.line_total_cents`. Mandar el
             * precio desde aqui seria dejar que el navegador fije lo que se
             * cobra, que es exactamente lo que el §10 prohibe para la venta a
             * dinero del granel y por el mismo motivo.
             */
            wholesale: i.wholesale ?? false,
          })),
          p_payment_method: body.paymentMethod,
          p_idempotency_key: body.idempotencyKey,
          p_discount_cents: body.discountCents ?? 0,
          p_cash_received_cents: body.cashReceivedCents ?? null,
          p_customer_id: body.customerId ?? null,
          p_note: body.note ?? null,
          p_credit_override: body.creditOverride ?? false,
          // Lo que el cliente deja enfrente al fiar. La base lo asienta como
          // abono en el libro de credito y lo mete al cajon en la misma
          // transaccion, para que el corte lo espere.
          p_paid_now_cents: body.paidNowCents ?? 0,
        }),
      );
    }
    if (s3 === 'sales' && s5 === 'cancel') {
      return out(
        await rpc('cancel_sale', {
          p_store_id: storeId,
          p_sale_id: Number(s4),
          p_reason: body.reason,
        }),
      );
    }
    if (s3 === 'sales' && s4) {
      const rows = await unwrap<Row[]>(
        supabase
          .from('sales')
          .select(`${SALE_COLUMNS}, ${SALE_USER}, customers(name)`)
          .eq('store_id', storeId)
          .eq('id', Number(s4))
          .limit(1),
      );
      if (!rows[0]) throw new ApiError('NOT_FOUND', 'No se encontro la venta.', 404);
      const items = await unwrap<Row[]>(
        supabase.from('sale_items').select(SALE_ITEM_COLUMNS).eq('sale_id', Number(s4)).order('id'),
      );

      /*
       * El abono que dejo al fiar no vive en `sales`: vive en el libro de
       * credito, que es donde esta la verdad de la deuda. Se reconoce por la
       * llave de idempotencia que le pone `create_sale`. Sin esto, el historial
       * de una venta fiada con abono diria que no dejo nada.
       */
      let downPaymentCents = 0;
      if (rows[0].payment_method === 'credit') {
        const down = await unwrap<Row[]>(
          supabase
            .from('customer_credit_movements')
            .select('amount_cents')
            .eq('sale_id', Number(s4))
            .eq('idempotency_key', `down:${Number(s4)}`),
        );
        downPaymentCents = down.reduce((acc, m) => acc + Number(m.amount_cents ?? 0), 0);
      }

      return out({
        sale: { ...mapSale(rows[0]), downPaymentCents, items: items.map(mapSaleItem) },
      });
    }
    if (s3 === 'sales') {
      let query = supabase
        .from('sales')
        // Sin `sale_items(count)`: ver `SALE_ITEM_COUNT_NOTE` mas abajo.
        .select(`${SALE_COLUMNS}, ${SALE_USER}, customers(name)`)
        .eq('store_id', storeId)
        .order('created_at', { ascending: false })
        .limit(Number(q.limit ?? 50));
      if (q.from) query = query.gte('business_date', String(q.from));
      if (q.to) query = query.lte('business_date', String(q.to));
      const rows = await unwrap<Row[]>(query);

      /*
       * SALE_ITEM_COUNT_NOTE — por que el conteo va en su propia consulta.
       *
       * `sale_items(count)` incrustado devolvia `403 permission denied for
       * table sale_items` y tumbaba la lista ENTERA. No es la RLS ni son las
       * columnas que se piden: PostgREST arma toda incrustacion con una
       * REFERENCIA DE FILA COMPLETA (`to_jsonb(sale_items)`), y una referencia
       * de fila completa exige privilegio de SELECT sobre LA TABLA. Los
       * privilegios por columna —los que esconden `unit_cost_cents`— no
       * cuentan para eso.
       *
       * O sea que `sales`, `sale_items`, `store_products` e
       * `inventory_movements`, las cuatro con el candado del costo, NO SE
       * PUEDEN INCRUSTAR desde PostgREST por ningun camino. Darles el privilegio
       * de tabla para arreglarlo reabriria la fuga de costo, porque un GRANT de
       * tabla cubre todas las columnas.
       *
       * Una segunda consulta con LISTA DE COLUMNAS no tiene ese problema: no
       * hay fila completa que serializar, solo las columnas que se nombran.
       * Trae los renglones de las ventas que ya se leyeron y la RLS de
       * `sale_items` ya hereda de su venta, asi que no expone nada que la lista
       * no mostrara.
       *
       * Y trae el NOMBRE, no solo el conteo. "Articulos: 1" no dice nada de una
       * venta: para saber que fue hay que abrir el detalle, venta por venta.
       * Con el nombre, el historial contesta de un vistazo la pregunta con la
       * que se abre esta pantalla —"¿de que fue esa venta?"—. `name_snapshot`
       * es el nombre CONGELADO al cobrar, que es justo el que hay que enseñar:
       * si el catalogo se renombro despues, el historico no cambia.
       *
       * Del costo: `unit_cost_cents` NO se pide y no puede pedirse por aqui
       * —`authenticated` no tiene SELECT sobre esa columna—. El renglon que
       * viaja es lo que ya se ve en el ticket del cliente.
       */
      const counts = new Map<number, number>();
      const previews = new Map<number, Row[]>();
      if (rows.length > 0) {
        const items = await unwrap<Row[]>(
          supabase
            .from('sale_items')
            .select('sale_id, name_snapshot, qty_milli, unit_snapshot')
            // Orden del ticket: el resumen tiene que coincidir con lo que
            // enseña el detalle al abrirlo, o parecen dos ventas distintas.
            .order('id')
            .in('sale_id', rows.map((r) => r.id as number)),
        );
        for (const item of items) {
          const id = item.sale_id as number;
          counts.set(id, (counts.get(id) ?? 0) + 1);
          // Solo los primeros: el resto lo resume `itemCount` con un "+N mas".
          // Guardar los treinta y ocho renglones de una venta grande para
          // enseñar tres seria cargar la lista de datos que nadie va a leer.
          const shown = previews.get(id) ?? [];
          if (shown.length < SALE_PREVIEW_ITEMS) {
            shown.push(item);
            previews.set(id, shown);
          }
        }
      }

      return out({
        sales: rows.map((r) => ({
          ...mapSale(r),
          itemCount: counts.get(r.id as number) ?? 0,
          items: (previews.get(r.id as number) ?? []).map((i) => ({
            nameSnapshot: i.name_snapshot,
            qtyMilli: i.qty_milli,
            unitSnapshot: i.unit_snapshot,
          })),
        })),
      });
    }

    // --- caja -------------------------------------------------------------
    if (s3 === 'cash' && s4 === 'open') {
      return out({
        session: await rpc('open_cash_session', {
          p_store_id: storeId,
          p_opening_cents: body.openingCents ?? 0,
          p_note: body.note ?? null,
        }),
      });
    }
    /*
     * SOLO SI SE PUEDE COBRAR.
     *
     * Es lo unico que la Caja necesita saber del cajon, y lo vuelve a preguntar
     * despues de CADA venta. Con `cash/current` eso eran cinco lecturas —una de
     * ellas un viaje al servidor de identidad— para leer un booleano; medido en
     * la tienda el 2 de septiembre de 2026, 240 veces al dia. `cash_drawer_status`
     * no trae un peso (§10), asi que le contesta igual al empleado que a la dueña.
     */
    if (s3 === 'cash' && s4 === 'drawer') {
      return out({ drawer: await rpc('cash_drawer_status', { p_store_id: storeId }) });
    }
    if (s3 === 'cash' && s4 === 'current') {
      /*
       * EL CAJON ABIERTO DE LA SUCURSAL, NO "MI TURNO".
       *
       * Antes esto filtraba `user_id = auth.uid()`, y con un solo rol capaz de
       * abrir caja daba igual. Desde que el empleado tambien abre (14 de agosto
       * de 2026) ya no: la dueña preguntaba por SU turno, no lo tenia, y la Caja
       * le decia "La caja esta cerrada" con el cajon abierto por el empleado
       * enfrente. El siguiente toque abria un SEGUNDO turno sobre el mismo
       * cajon fisico, con su propio fondo declarado sobre el mismo dinero.
       *
       * No hace falta filtrar por permiso aqui: la RLS de `cash_sessions` es
       * `user_id = auth.uid() or has_store_permission('cash:view_all')`, asi que
       * quien puede ver el turno ajeno lo ve, y quien no, sigue viendo solo el
       * suyo. Una condicion mas en el cliente seria una segunda regla de acceso
       * que un dia dice algo distinto que la de la base.
       *
       * `mine` viaja aparte porque no es lo mismo "hay un cajon abierto" que "es
       * el mio": lo segundo decide si se ofrece cerrarlo.
       *
       * El orden es ascendente para nombrar el MISMO turno que reporta
       * `open_cash_session` al rechazar una segunda apertura. Hoy solo puede
       * haber uno; si una fila vieja dejara dos, las dos pantallas hablarian del
       * mismo y no una de cada uno.
       */
      /*
       * Quien soy sale de la sesion que YA tengo en la mano, no de preguntarselo
       * al servidor de identidad. `getUser()` valida el token contra GoTrue en
       * cada llamada —un viaje de red de 180 ms de media, 415 en el p95— y esta
       * ruta se pedia tras cada venta. Las filas que siguen ya vienen filtradas
       * por RLS con ese mismo uid, asi que volver a validarlo no agrega nada.
       */
      const { data: auth } = await supabase.auth.getSession();
      const myId = auth.session?.user.id;
      const sessions = await unwrap<Row[]>(
        supabase
          .from('cash_sessions')
          .select('*, users(full_name)')
          .eq('store_id', storeId)
          .is('closed_at', null)
          .order('opened_at', { ascending: true })
          .limit(1),
      );
      const session = sessions[0];
      if (!session) {
        /*
         * No veo ningun turno abierto, y eso son DOS cosas distintas: que no
         * haya, o que no me toque verlo. `cash_drawer_status` las distingue sin
         * enseñar un solo peso — solo si esta abierto, quien lo abrio y cuando.
         * Sin esto, al empleado le decia "La caja esta cerrada" todo el dia con
         * el cajon de la dueña abierto enfrente.
         */
        const drawer = await rpc('cash_drawer_status', { p_store_id: storeId });
        return out({ session: null, expectation: null, movements: [], mine: false, drawer });
      }

      // Vista previa del corte. Es una LECTURA: el corte que manda lo calcula la
      // base dentro de `close_cash_session`, con los mismos criterios.
      const [sales, movements, expenses] = await Promise.all([
        unwrap<Row[]>(
          supabase.from('sales').select('total_cents, payment_method, status').eq('cash_session_id', session.id),
        ),
        unwrap<Row[]>(
          supabase.from('cash_movements').select('*').eq('cash_session_id', session.id).order('created_at'),
        ),
        unwrap<Row[]>(
          supabase
            .from('expenses')
            .select('amount_cents')
            .eq('cash_session_id', session.id)
            .eq('paid_from_cash_drawer', true),
        ),
      ]);

      const done = sales.filter((s) => s.status === 'completed');
      const sum = (rows: Row[], field: string): number =>
        rows.reduce((acc, r) => acc + Number(r[field] ?? 0), 0);

      const cashSalesCents = sum(done.filter((s) => s.payment_method === 'cash'), 'total_cents');
      const nonCashSalesCents = sum(done.filter((s) => s.payment_method !== 'cash'), 'total_cents');
      const cashInCents = sum(movements.filter((m) => m.type === 'in'), 'amount_cents');
      const cashOutCents = sum(movements.filter((m) => m.type === 'out'), 'amount_cents');
      const cashExpensesCents = sum(expenses, 'amount_cents');

      return out({
        session: mapCashSession(session),
        mine: session.user_id === myId,
        // Si llegue a ver la fila, ya se todo lo que la RPC diria; preguntarselo
        // seria un viaje de red para repetir lo que tengo en la mano.
        drawer: {
          open: true,
          openedByName: session.users?.full_name ?? null,
          openedAt: session.opened_at,
          mine: session.user_id === myId,
        },
        expectation: {
          openingCents: session.opening_cents,
          cashSalesCents,
          cashInCents,
          cashOutCents,
          cashExpensesCents,
          nonCashSalesCents,
          salesCount: done.length,
          expectedCents:
            session.opening_cents + cashSalesCents + cashInCents - cashOutCents - cashExpensesCents,
        },
        movements: movements.map(mapCashMovement),
      });
    }
    if (s3 === 'cash' && s4 === 'sessions' && !s5) {
      const rows = await unwrap<Row[]>(
        supabase
          .from('cash_sessions')
          .select('*, users(full_name)')
          .eq('store_id', storeId)
          .order('opened_at', { ascending: false })
          .limit(Number(q.limit ?? 50)),
      );
      return out({ sessions: rows.map(mapCashSession) });
    }
    if (s3 === 'cash' && s5 === 'close') {
      return out(
        await rpc('close_cash_session', {
          p_store_id: storeId,
          p_session_id: Number(s4),
          p_counted_cents: body.countedCents,
          p_note: body.note ?? null,
        }),
      );
    }
    /*
     * El corte del dia, armado entero por la base.
     *
     * Dos formas de pedirlo y una sola funcion: con turno (`.../cash/12/report`)
     * para reimprimir un corte cerrado, y sin turno (`.../cash/report`) para el
     * del turno abierto. Cuando no se indica, la base elige —el turno abierto de
     * quien pregunta, o el ultimo del dia— porque elegirlo aqui obligaria a
     * hacer una consulta previa solo para averiguar cual es.
     *
     * `p_session_id` se manda EXPLICITAMENTE en null y no se omite: PostgREST
     * no distingue "sin argumento" de "argumento nulo" al buscar la firma, y
     * omitirlo funciona por el `default null` de la funcion, no por diseño.
     */
    if (s3 === 'cash' && s4 === 'report') {
      return out({ report: await rpc('get_shift_report', { p_store_id: storeId, p_session_id: null }) });
    }
    if (s3 === 'cash' && s5 === 'report') {
      return out({
        report: await rpc('get_shift_report', { p_store_id: storeId, p_session_id: Number(s4) }),
      });
    }
    /*
     * QUITAR un movimiento de efectivo mal capturado. Solo mientras su turno
     * siga abierto; lo aplica la base. Un retiro de $500 donde iban $50 le
     * resta 450 pesos al efectivo esperado y el corte sale con un sobrante que
     * nadie puede explicar — y hasta hoy no habia forma de quitarlo.
     */
    if (s3 === 'cash' && s4 === 'movements' && s5 && method === 'DELETE') {
      return out(
        await rpc('delete_cash_movement', {
          p_store_id: storeId,
          p_movement_id: Number(s5),
        }),
      );
    }
    if (s3 === 'cash' && s5 === 'movements' && method === 'POST') {
      return out({
        movement: await rpc('add_cash_movement', {
          p_store_id: storeId,
          p_session_id: Number(s4),
          p_type: body.type,
          p_amount_cents: body.amountCents,
          p_reason: body.reason,
        }),
      });
    }

    // --- catalogo ---------------------------------------------------------
    // Va por RPC y no por lectura directa a proposito: asi el costo NO sale de
    // la base cuando quien pregunta no tiene `inventory:view_cost`. Filtrarlo
    // aqui no serviria de nada, porque el dato ya habria viajado.
    /*
     * Lo que hay que revisar del catalogo, y la forma de decir que ya se
     * reviso. Va por RPC y no por lectura directa porque la deteccion compara
     * el nombre NORMALIZADO de todo el catalogo contra si mismo: eso es una
     * consulta, no un filtro, y en el navegador exigiria bajarse el catalogo
     * entero para agruparlo a mano.
     */
    if (s3 === 'catalog' && s4 === 'issues' && !s5) {
      return out({ issues: await rpc('get_catalog_issues', { p_store_id: storeId }) });
    }
    if (s3 === 'catalog' && s4 === 'issues' && s5 === 'dismiss') {
      return out(
        await rpc('dismiss_catalog_duplicate', {
          p_store_id: storeId,
          // El nombre del parametro lo copia de la PANTALLA, no del argumento
          // SQL: ver la trampa de `p_code` / `barcode` en el CLAUDE.md §13.
          p_name_key: body.nameKey,
          p_reason: body.reason ?? null,
        }),
      );
    }
    if (s3 === 'catalog' && s4 === 'lookup') {
      // Un solo viaje: la base resuelve etiqueta de bascula, codigo de barras en
      // todas sus formas, y SKU tecleado, en ese orden.
      //
      // EL PARAMETRO SE LLAMA `barcode`, que es como lo manda la caja desde el
      // primer dia. Aqui decia `q.code` —el nombre del argumento de la funcion
      // SQL, `p_code`— y como ese parametro nunca llegaba, se enviaba cadena
      // vacia en TODOS los escaneos; `lookup_scan` la rechaza con
      // VALIDATION_ERROR y el cajero leia "Hay datos incorrectos en el
      // formulario" al escanear un refresco. Rompia el lector USB, el codigo
      // tecleado y la camara por igual, y no lo delataba ningun error de
      // compilacion porque `q` es un diccionario de nombres libres.
      //
      // La regla que se violo esta al principio de este archivo: la pasarela
      // CONSERVA la firma de rutas que usan las pantallas. Quien traduzca una
      // ruta nueva copia el nombre de la pantalla, no el de la funcion SQL.
      return out(
        await rpc('lookup_scan', { p_store_id: storeId, p_code: String(q.barcode ?? '') }),
      );
    }
    // El archivero. Va por su propia funcion y no por un parametro mas en
    // `get_catalog`: cambiarle la firma a la funcion que usa la caja para
    // atender una pantalla de administracion sale caro, y un parametro con
    // valor por omision crearia una sobrecarga que deja a PostgREST sin saber
    // cual de las dos llamar.
    if (s3 === 'catalog' && s4 === 'archived') {
      return out({
        items: await rpc('get_archived_catalog', {
          p_store_id: storeId,
          p_search: q.search ? String(q.search) : null,
          p_limit: Number(q.limit ?? 200),
          p_offset: Number(q.offset ?? 0),
        }),
      });
    }
    if (s3 === 'catalog' && s4) {
      return out({
        item: await rpc('get_catalog_item', { p_store_id: storeId, p_product_id: Number(s4) }),
      });
    }
    // Censo de unidades por categoria, para que el alta proponga como se cobra
    // en vez de dejar todo en "Pieza". Se cuenta en la base porque el catalogo
    // que baja esta pantalla viene truncado y un conteo hecho aqui saldria
    // sesgado por orden alfabetico sin avisar.
    /*
     * Lo que la caja pinta cuando NADIE ha buscado nada: los productos que mas
     * veces aparecen en un ticket, no el catalogo por orden alfabetico.
     *
     * Va por RPC aparte y no por `get_catalog` porque son dos preguntas
     * distintas —"que hay" contra "que voy a tocar ahorita"— y porque
     * agregarle un parametro a `get_catalog` crearia una sobrecarga (§13).
     * Devuelve la MISMA forma, asi que la pantalla no distingue una de otra.
     */
    /*
     * Con que cantidades se vende cada producto a granel, para que los atajos
     * de la hoja de cantidad sean los de ESTE mostrador y no cuatro fijos.
     *
     * Viaja el mapa COMPLETO de la sucursal en una sola lectura, junto con la
     * cuadricula, y no una consulta por producto al abrir la hoja: esa hoja se
     * abre en cuatro de cada cinco renglones y con el cliente enfrente. Una
     * pista que llega tarde es peor que no tenerla, porque los atajos
     * cambiarian bajo el dedo.
     */
    if (s3 === 'quantity-hints') {
      return out({
        hints: await rpc('get_quantity_hints', {
          p_store_id: storeId,
          p_days: Number(q.days ?? 60),
          p_per_product: Number(q.perProduct ?? 4),
        }),
      });
    }
    if (s3 === 'quick-picks') {
      return out({
        items: await rpc('get_quick_picks', {
          p_store_id: storeId,
          p_limit: Number(q.limit ?? 60),
          p_days: Number(q.days ?? 60),
        }),
      });
    }
    if (s3 === 'unit-hints') {
      return out(await rpc('get_unit_hints', { p_store_id: storeId }));
    }
    if (s3 === 'catalog') {
      return out({
        items: await rpc('get_catalog', {
          p_store_id: storeId,
          p_search: q.search ? String(q.search) : null,
          p_category_id: q.categoryId ? Number(q.categoryId) : null,
          // `onlyLowStock`, como lo manda `InventoryScreen`. Decia `q.lowStock`
          // —el nombre acortado— y el filtro "Solo bajo stock" del inventario no
          // hacia absolutamente nada: la casilla se prendia y la lista seguia
          // igual. Es el mismo descuido que el de `catalog/lookup`, de la misma
          // mudanza a Postgres.
          p_only_low_stock: Boolean(q.onlyLowStock),
          p_include_inactive: Boolean(q.inactive),
          p_limit: Number(q.limit ?? 200),
          p_offset: Number(q.offset ?? 0),
        }),
      });
    }

    // --- sucursales -------------------------------------------------------
    /*
     * La organizacion vista desde la sucursal activa.
     *
     * La sesion NO lleva `org_id` y no se le agrega: el negocio se deriva de la
     * sucursal en la que ya se esta parado, que es un dato que la RLS valida de
     * todos modos. Asi esta ruta cabe en la convencion `/api/stores/:id/...`
     * como todas las demas, en vez de estrenar una familia de rutas de
     * organizacion para tres pantallas.
     */
    /*
     * Va ANTES del GET de `organization` a proposito: aquel no mira `s4`, asi
     * que si se dejara despues, un POST caeria en la lectura y devolveria la
     * lista tan tranquilo en vez de dar de alta nada. El orden de las ramas ES
     * la ruta en esta pasarela.
     */
    if (s3 === 'organization' && s4 === 'stores' && method === 'POST') {
      return out(
        await rpc('create_store', {
          p_org_id: body.orgId,
          p_name: body.name,
          /*
           * La CLAVE ya no se pregunta en la pantalla, y por eso viaja nula: la
           * acuña `create_store` a partir del nombre (SUR, y SUR2 si esa ya
           * existe), igual que los dos prefijos de codigo de barras. Se sigue
           * mandando lo que venga en el cuerpo para que la semilla y cualquier
           * script puedan elegirla; nula significa "acuñala tu".
           */
          p_code: body.code ?? null,
          p_timezone: body.timezone ?? 'America/Mexico_City',
          p_address: body.address ?? null,
          p_phone: body.phone ?? null,
          p_day_start_hour: body.dayStartHour ?? 0,
          p_cash_rounding_cents: body.cashRoundingCents ?? 0,
        }),
      );
    }
    /*
     * Eran dos lecturas de PostgREST cosidas aqui, y se fueron a una RPC porque
     * la pantalla dejo de preguntar por la plomeria de la sucursal —clave y
     * prefijos— y empezo a preguntar por su operacion: cuantos productos tiene
     * y cuanto lleva vendido hoy. Eso son agregados sobre `sales` y
     * `store_products`, que estan bajo RLS por membresia y ademas con
     * privilegios por columna: contarlos desde el navegador seria traerse las
     * filas para contarlas. El calculo va junto a los datos (CLAUDE.md §11).
     *
     * De paso el conteo del limite dejo de ser cosmetico: `get_organization`
     * cuenta las sucursales del NEGOCIO —las mismas que cuenta `create_store`
     * para aceptar o rechazar—, no las que la RLS deja ver.
     */
    if (s3 === 'organization') {
      return out(await rpc('get_organization', { p_store_id: storeId }));
    }
    // --- productos --------------------------------------------------------
    /*
     * Aqui vivia `products/adopt`, "traer a esta sucursal" un producto del
     * negocio. Se retiro el 22 de agosto de 2026 junto con la RPC: el catalogo
     * dejo de ser del negocio y ahora es de cada sucursal, asi que no hay de
     * donde traer nada — cada tienda captura lo suyo. Ver la migracion
     * 20260822061107.
     */
    if (s3 === 'barcodes' && s4 === 'next') {
      return out({ barcode: await rpc<string>('next_internal_barcode', { p_store_id: storeId }) });
    }
    /*
     * De quien es un codigo que ya esta dado de alta.
     *
     * Contesta `null` cuando esta libre, y eso NO es un caso de error: es la
     * respuesta corriente. El alta pregunta esto en cuanto recibe un codigo para
     * ofrecer resurtir en vez de rechazar al final.
     *
     * El parametro se llama `barcode`, como lo manda la pantalla —no `p_code`,
     * que es el nombre del argumento SQL—. Ver la trampa del CLAUDE.md §13: `q`
     * es un diccionario de nombres libres y el compilador no ve la diferencia.
     */
    if (s3 === 'barcodes' && s4 === 'owner') {
      return out({
        owner: await rpc<Row | null>('lookup_barcode_owner', {
          p_store_id: storeId,
          p_code: String(q.barcode ?? ''),
        }),
      });
    }
    if (s3 === 'barcodes' && s4 === 'settings') {
      if (method === 'PATCH') {
        return out(
          await rpc('update_store_barcodes', {
            p_store_id: storeId,
            p_internal_prefix: body.internalBarcodePrefix ?? null,
            p_scale_mode: body.scaleMode ?? null,
            p_scale_prefix: body.scalePrefix ?? null,
            p_scale_item_digits: body.scaleItemDigits ?? null,
            p_scale_value_digits: body.scaleValueDigits ?? null,
          }),
        );
      }
      const rows = await unwrap<Row[]>(
        supabase
          .from('stores')
          .select('internal_barcode_prefix, scale_mode, scale_prefix, scale_item_digits, scale_value_digits')
          .eq('id', storeId)
          .limit(1),
      );
      const r = rows[0] ?? {};
      return out({
        internalPrefix: r.internal_barcode_prefix ?? '20',
        scale: {
          mode: r.scale_mode ?? 'off',
          prefix: r.scale_prefix ?? '21',
          itemDigits: r.scale_item_digits ?? 5,
          valueDigits: r.scale_value_digits ?? 5,
        },
      });
    }
    /*
     * CORREGIR EL COSTO va por su propia RPC y no dentro de `pricing`, y no es
     * simetria mal entendida: son permisos distintos. Poner precios pide
     * `product:update`; tocar el costo pide ademas `inventory:view_cost`,
     * porque quien no puede VER el costo tampoco debe poder escribirlo —
     * escribir y mirar el efecto es una forma de leerlo—. La regla vive en la
     * base, como todas; aqui solo se traduce.
     */
    if (s3 === 'products' && s5 === 'cost') {
      return out({
        item: await rpc('set_store_product_cost', {
          p_store_id: storeId,
          p_product_id: Number(s4),
          p_cost_cents: body.costCents,
          p_reason: body.reason,
        }),
      });
    }
    if (s3 === 'products' && s5 === 'pricing') {
      return out({
        item: await rpc('set_store_product_pricing', {
          p_store_id: storeId,
          p_product_id: Number(s4),
          p_price_cents: body.priceCents ?? null,
          /*
           * El mayoreo necesita DOS campos y no uno, porque aqui `null` ya
           * significa "no cambies esto" —toda la funcion es `coalesce`— y no
           * puede significar tambien "borralo". `clearWholesale` es la unica
           * forma de decir "este producto deja de tener mayoreo"; es el mismo
           * patron de `clearCategory` en la edicion de producto.
           */
          p_wholesale_price_cents: body.wholesalePriceCents ?? null,
          p_clear_wholesale: body.clearWholesale ?? false,
          p_min_stock_milli: body.minStockMilli ?? null,
          p_max_stock_milli: body.maxStockMilli ?? null,
          p_is_available: body.isAvailable ?? null,
        }),
      });
    }
    /*
     * Ultimas entradas de UN producto, para tenerlas enfrente al capturar la
     * siguiente. "La vez pasada te lo dieron a $120 hace nueve dias" es lo que
     * convierte la captura en una revision.
     *
     * Se pide desde `purchases` y no desde `purchase_items` porque el orden que
     * importa es el de la compra, y ordenar por una columna incrustada es
     * terreno que PostgREST resuelve distinto segun la version. Al reves sale
     * gratis: `purchase_items!inner` filtrado por producto recorta las dos
     * cosas de un golpe —la compra sin ese producto no aparece, y de la que si
     * aparece solo viaja su renglon—.
     *
     * Y estas dos tablas SI se pueden incrustar, a diferencia de `sale_items`:
     * conservan el privilegio de SELECT de tabla. Lo que esconde el costo de
     * compra no es un candado de columna sino la politica RLS, que exige
     * `purchase:manage`. Un cajero recibe cero filas, no un 403.
     */
    if (s3 === 'products' && s4 && s5 === 'purchases') {
      const rows = await unwrap<Row[]>(
        supabase
          .from('purchases')
          .select(
            `id, folio, reference, business_date, created_at, suppliers(name), purchase_items!inner(qty_milli, unit_cost_cents, product_id)`,
          )
          .eq('store_id', storeId)
          .eq('purchase_items.product_id', Number(s4))
          // Una entrada cancelada no es "la vez pasada te lo dieron a $120":
          // es una que no ocurrio. Dejarla aqui haria capturar contra un costo
          // que ya se retiro del promedio.
          .eq('status', 'completed')
          .order('created_at', { ascending: false })
          .limit(Number(q.limit ?? 5)),
      );
      return out({
        purchases: rows.flatMap((r) =>
          ((r.purchase_items ?? []) as Row[]).map((line) => ({
            purchaseId: r.id,
            folio: r.folio,
            supplierName: r.suppliers?.name ?? null,
            reference: r.reference,
            qtyMilli: line.qty_milli,
            unitCostCents: line.unit_cost_cents,
            businessDate: r.business_date,
            createdAt: r.created_at,
          })),
        ),
      });
    }

    // --- baja de producto -------------------------------------------------
    // El expediente lo arma la base y ahi se decide si el producto se puede
    // borrar o solo descontinuar. Aqui no se decide nada: se traduce.
    if (s3 === 'products' && s5 === 'usage') {
      return out({
        usage: await rpc('get_product_usage', { p_store_id: storeId, p_product_id: Number(s4) }),
      });
    }
    /*
     * La salida de la unidad bloqueada: archiva el viejo, le MUEVE los codigos
     * y crea el corregido, todo en una transaccion. Ver la migracion
     * `replace_product_unit`.
     */
    if (s3 === 'products' && s5 === 'replace-unit') {
      return out({
        item: await rpc('replace_product_unit', {
          p_store_id: storeId,
          p_product_id: Number(s4),
          p_unit: body.unit,
          p_is_weighed: body.isWeighed,
          p_reason: body.reason,
          p_initial_stock_milli: body.initialStockMilli ?? 0,
          p_shrinkage_bp_monthly: body.shrinkageBpMonthly ?? null,
        }),
      });
    }
    if (s3 === 'products' && s5 === 'archive') {
      return out({
        item: await rpc('archive_product', {
          p_store_id: storeId,
          p_product_id: Number(s4),
          p_write_off_stock: Boolean(body.writeOffStock),
          p_reason: body.reason ?? null,
        }),
      });
    }
    if (s3 === 'products' && s5 === 'restore') {
      return out({
        item: await rpc('restore_product', { p_store_id: storeId, p_product_id: Number(s4) }),
      });
    }
    if (s3 === 'products' && s4 && method === 'DELETE') {
      return out(
        await rpc('delete_product', {
          p_store_id: storeId,
          p_product_id: Number(s4),
          // La existencia que quede se cierra con su renglon de kardex, igual
          // que al descontinuar. Va explicito y no confiado al valor por
          // omision de la RPC: el nombre lo copia de la pantalla, no de SQL.
          p_write_off_stock: body.writeOffStock !== false,
        }),
      );
    }
    if (s3 === 'products' && s4 && method === 'PATCH') {
      return out({
        item: await rpc('update_product', {
          p_store_id: storeId,
          p_product_id: Number(s4),
          p_name: body.name ?? null,
          // La CLAVE CORTA. `null` es "no la toques"; la base rechaza la cadena
          // vacia en vez de tomarla como borrado, porque un producto sin clave
          // no existe. Se acepta desde el 22 de agosto de 2026: era el ultimo
          // campo del alta sin camino de vuelta.
          p_sku: body.sku ?? null,
          p_brand: body.brand ?? null,
          p_category_id: body.categoryId ?? null,
          p_unit: body.unit ?? null,
          p_is_weighed: body.isWeighed ?? null,
          p_pack_size: body.packSize ?? null,
          p_shrinkage_bp_monthly: body.shrinkageBpMonthly ?? null,
          p_scale_item_code: body.scaleItemCode ?? null,
          p_is_active: body.isActive ?? null,
          p_clear_category: body.categoryId === null,
          p_replace_barcodes: body.barcodes !== undefined,
          p_barcodes: ((body.barcodes as string[]) ?? []).map(normalizeBarcode),
        }),
      });
    }
    /*
     * `!s4` NO es rigor de mas: sin el, CUALQUIER `POST /products/<loquesea>`
     * cae aqui y se trata como un alta.
     *
     * Dos trabajos distintos llegaron a esta misma linea el 22 de agosto de 2026,
     * cada uno con su propia evidencia, y las dos merecen quedar escritas:
     *
     *  - Las rutas con `s5` funcionaban solo por el ORDEN en que estan
     *    declaradas, y el orden es una propiedad fragil: la siguiente ruta que
     *    alguien escriba encima vuelve a taparlas. Ese defecto exacto dejo el
     *    boton de cancelar una entrada muerto desde que se escribio.
     *  - Y estuvo tapado tambien por `products/adopt`, que iba primero y se lo
     *    tragaba; al retirar esa ruta quedo al descubierto, y lo cazo la prueba
     *    que exige 404 para la ruta retirada.
     *
     * Una ruta que se traga lo que no es suyo no falla: hace otra cosa, que es
     * la forma cara de fallar. Aqui la condicion lo dice sola.
     */
    if (s3 === 'products' && !s4 && method === 'POST') {
      return out({
        item: await rpc('create_product', {
          p_store_id: storeId,
          p_sku: body.sku,
          p_name: body.name,
          p_unit: body.unit,
          p_is_weighed: body.isWeighed ?? false,
          p_price_cents: body.priceCents ?? 0,
          // `null` y no cero: es "sin mayoreo", no "mayoreo de cero pesos".
          p_wholesale_price_cents: body.wholesalePriceCents ?? null,
          p_cost_cents: body.costCents ?? 0,
          p_initial_stock_milli: body.initialStockMilli ?? 0,
          p_min_stock_milli: body.minStockMilli ?? 0,
          p_max_stock_milli: body.maxStockMilli ?? 0,
          p_brand: body.brand ?? null,
          p_category_id: body.categoryId ?? null,
          p_pack_size: body.packSize ?? 1,
          p_shrinkage_bp_monthly: body.shrinkageBpMonthly ?? 0,
          p_scale_item_code: body.scaleItemCode ?? null,
          p_barcodes: ((body.barcodes as string[]) ?? []).map(normalizeBarcode),
        }),
      });
    }

    // --- inventario -------------------------------------------------------
    if (s3 === 'inventory' && s4 === 'count-preview') {
      return out({
        preview: await rpc('preview_count', {
          p_store_id: storeId,
          p_product_id: body.productId,
          p_counted_stock_milli: body.countedStockMilli,
        }),
      });
    }
    if (s3 === 'inventory' && s4 === 'adjust') {
      return out(
        await rpc('adjust_stock', {
          p_store_id: storeId,
          p_product_id: body.productId,
          p_counted_stock_milli: body.countedStockMilli,
          p_reason: body.reason,
        }),
      );
    }
    if (s3 === 'inventory' && s4 === 'shrinkage') {
      return out(
        await rpc('register_shrinkage', {
          p_store_id: storeId,
          p_product_id: body.productId,
          p_qty_milli: body.qtyMilli,
          p_reason: body.reason,
        }),
      );
    }
    if (s3 === 'inventory' && s4 === 'movements') {
      let query = supabase
        .from('inventory_movements')
        .select(`${MOVEMENT_COLUMNS}, products(name, unit, is_weighed), users(full_name)`)
        .eq('store_id', storeId)
        .order('created_at', { ascending: false })
        .limit(Number(q.limit ?? 100));
      if (q.productId) query = query.eq('product_id', Number(q.productId));
      if (q.type) query = query.eq('type', String(q.type));
      return out({ movements: (await unwrap<Row[]>(query)).map(mapMovement) });
    }

    // --- gastos -----------------------------------------------------------
    /*
     * Corregir un gasto ya registrado. Lo que NO viaja es si salio o no del
     * cajon: `update_expense` no lo acepta, porque cambiarlo tendria que
     * cargarle a la caja de HOY un dinero que salio otro dia. Ver la migracion.
     */
    if (s3 === 'expenses' && s4 && method === 'PATCH') {
      return out({
        expense: await rpc('update_expense', {
          p_store_id: storeId,
          p_expense_id: Number(s4),
          p_category: body.category,
          p_description: body.description ?? null,
          p_amount_cents: body.amountCents,
          p_recurrence: body.recurrence ?? null,
        }),
      });
    }
    if (s3 === 'expenses' && s4 && method === 'DELETE') {
      return out(await rpc('delete_expense', { p_store_id: storeId, p_expense_id: Number(s4) }));
    }
    if (s3 === 'expenses' && method === 'POST') {
      return out({
        expense: await rpc('create_expense', {
          p_store_id: storeId,
          p_category: body.category,
          // La descripcion es opcional; la RPC guarda null cuando llega vacia.
          p_description: body.description ?? null,
          p_amount_cents: body.amountCents,
          p_paid_from_cash_drawer: body.paidFromCashDrawer ?? true,
          p_recurrence: body.recurrence ?? 'one_time',
        }),
      });
    }
    if (s3 === 'expenses') {
      let query = supabase
        .from('expenses')
        .select('*, users(full_name)')
        .eq('store_id', storeId)
        .order('created_at', { ascending: false })
        .limit(Number(q.limit ?? 50));
      if (q.from) query = query.gte('business_date', String(q.from));
      if (q.to) query = query.lte('business_date', String(q.to));
      if (q.category) query = query.eq('category', String(q.category));
      return out({ expenses: (await unwrap<Row[]>(query)).map(mapExpense) });
    }

    // --- credito ----------------------------------------------------------
    if (s3 === 'credit') {
      return out({ report: await rpc('get_credit_report', { p_store_id: storeId }) });
    }
    if (s3 === 'customers' && s5 === 'credit-check') {
      return out(
        await rpc('check_credit', {
          p_store_id: storeId,
          p_customer_id: Number(s4),
          p_amount_cents: Number(q.amountCents ?? 0),
        }),
      );
    }
    if (s3 === 'customers' && s5 === 'payments') {
      return out(
        await rpc('register_payment', {
          p_store_id: storeId,
          p_customer_id: Number(s4),
          p_amount_cents: body.amountCents,
          p_idempotency_key: body.idempotencyKey,
          p_into_cash_drawer: body.intoCashDrawer ?? true,
          p_note: body.note ?? null,
        }),
      );
    }
    if (s3 === 'customers' && s5 === 'adjustments') {
      return out(
        await rpc('adjust_credit', {
          p_store_id: storeId,
          p_customer_id: Number(s4),
          p_delta_cents: body.deltaCents,
          p_reason: body.reason,
        }),
      );
    }
    if (s3 === 'customers' && s4 && method === 'PATCH') {
      return out({
        customer: await rpc('update_customer', {
          p_store_id: storeId,
          p_customer_id: Number(s4),
          p_name: body.name ?? null,
          p_phone: body.phone ?? null,
          p_notes: body.notes ?? null,
          p_credit_limit_cents: body.creditLimitCents ?? null,
          p_credit_days: body.creditDays ?? null,
          p_is_active: body.isActive ?? null,
          p_clear_limit: body.creditLimitCents === null,
        }),
      });
    }
    if (s3 === 'customers' && method === 'POST') {
      return out({
        customer: await rpc('create_customer', {
          p_store_id: storeId,
          p_name: body.name,
          p_phone: body.phone ?? null,
          p_notes: body.notes ?? null,
          p_credit_limit_cents: body.creditLimitCents ?? null,
          p_credit_days: body.creditDays ?? 15,
        }),
      });
    }
    if (s3 === 'customers' && s4) {
      const rows = await unwrap<Row[]>(
        supabase.from('customers').select('*').eq('id', Number(s4)).limit(1),
      );
      if (!rows[0]) throw new ApiError('NOT_FOUND', 'No se encontro el cliente.', 404);
      const movements = await unwrap<Row[]>(
        supabase
          .from('customer_credit_movements')
          .select('*, users(full_name)')
          .eq('customer_id', Number(s4))
          .order('id', { ascending: false })
          .limit(Number(q.limit ?? 50)),
      );
      return out({
        customer: mapCustomer(rows[0]),
        movements: movements.map(mapCreditMovement),
      });
    }
    if (s3 === 'customers') {
      let query = supabase.from('customers').select('*').order('balance_cents', { ascending: false });
      if (!q.inactive) query = query.eq('is_active', true);
      if (q.debtors) query = query.gt('balance_cents', 0);
      if (q.search) query = query.ilike('search_text', `%${normalizeSearch(String(q.search))}%`);
      const rows = await unwrap<Row[]>(query.limit(Number(q.limit ?? 50)));
      return out({ customers: rows.map(mapCustomer) });
    }

    /*
     * LOS TRASPASOS ENTRE SUCURSALES SE RETIRARON el 19 de agosto de 2026.
     *
     * Nunca tuvieron pantalla y nunca se uso ninguno —cero traspasos, cero
     * renglones, cero movimientos de kardex—. El negocio no los necesita: cada
     * sucursal vende productos distintos, y una sucursal nueva se surte dando
     * de alta SUS PROPIOS productos y registrando su entrada de mercancia. (La
     * adopcion entre sucursales, que fue el camino entre el 18 y el 22 de
     * agosto de 2026, se retiro tambien con el catalogo por sucursal.)
     *
     * Se fueron con todo: las dos tablas, `create_transfer`, `receive_transfer`,
     * los permisos `transfer:*` y los tipos de kardex `transfer_in`/`out`.
     * Arrastraban ademas una fuga de costo —`transfer_items` se leia por
     * organizacion SIN comprobar permiso, y con `select` de tabla, asi que
     * `unit_cost_cents` viajaba a quien no puede ver costo—. Retirarlos la
     * cierra por construccion en vez de por remiendo. Ver la migracion del
     * 19 de agosto de 2026.
     */

    // --- personal y sucursal ----------------------------------------------
    if (s3 === 'staff' && s4 === 'activity') {
      return out(await rpc('get_staff_activity', { p_store_id: storeId, p_days: Number(q.days ?? 7) }));
    }
    // Alta, cambio de contrasena y borrado pasan por la Edge Function porque las
    // tres tocan `auth.users`, y eso necesita la llave de servicio — que jamas
    // puede viajar al navegador. Van con el JWT de quien las pide: alla adentro,
    // el permiso y la auditoria los resuelve Postgres antes de que la llave de
    // servicio haga nada. Ver `supabase/functions/invite-staff/index.ts`.
    if (s3 === 'staff' && s4 === 'invite' && method === 'POST') {
      return out(await staffFunction({ ...body, action: 'invite', storeId }));
    }
    if (s3 === 'staff' && s4 && s5 === 'password' && method === 'POST') {
      return out(
        await staffFunction({ action: 'set-password', storeId, userId: s4, password: body.password }),
      );
    }
    if (s3 === 'staff' && s4 && method === 'DELETE') {
      return out(await staffFunction({ action: 'delete', storeId, userId: s4 }));
    }

    if (s3 === 'staff' && s4 && s5 === 'usage') {
      return out(await rpc('get_user_usage', { p_store_id: storeId, p_user_id: s4 }));
    }
    if (s3 === 'staff' && s4 && s5 === 'role' && method === 'POST') {
      return out(
        await rpc('set_store_membership', {
          p_store_id: storeId,
          p_user_id: s4,
          p_role: body.role,
        }),
      );
    }
    // Quitar el acceso NO borra la cuenta: sale de esta sucursal y su historia
    // se queda. Es la ruta que se ofrece por omision; borrar es aparte y solo
    // procede si esa persona nunca movio nada.
    if (s3 === 'staff' && s4 && s5 === 'access' && method === 'DELETE') {
      return out(await rpc('remove_store_membership', { p_store_id: storeId, p_user_id: s4 }));
    }
    if (s3 === 'settings' && method === 'PATCH') {
      return out(
        await rpc('update_store', {
          p_store_id: storeId,
          p_name: body.name ?? null,
          // `code`, el nombre que manda la PANTALLA. La base la sube a
          // mayusculas y rechaza una repetida con `STORE_CODE_TAKEN`; aqui solo
          // se traduce. Vacia significa "no la toques", no "borrala".
          p_code: body.code ?? null,
          p_address: body.address ?? null,
          p_phone: body.phone ?? null,
          /*
           * APAGAR Y ENCENDER una sucursal. La base lo sabia hacer desde
           * siempre y la pantalla ya pintaba el estado "Apagada", pero este
           * campo no viajaba: era un estado dibujado al que no se llegaba por
           * ningun camino. Se conecto el 9 de septiembre de 2026 junto con
           * eliminar, porque apagar es la salida REVERSIBLE que hace
           * responsable a la irreversible.
           *
           * `??` y no `||`: `false` es un valor legitimo —es justamente apagar—
           * y con `||` se convertiria en "no la toques".
           */
          p_is_active: body.isActive ?? null,
        }),
      );
    }
    /*
     * El expediente de una sucursal, para la hoja de dar de baja. Sin cache en
     * la pantalla: de estos numeros depende que se ofrezca eliminar.
     */
    if (s3 === 'store-usage' && !s4) {
      return out({ usage: await rpc('get_store_usage', { p_store_id: storeId }) });
    }
    /*
     * Eliminar la sucursal. `!s3` EXIGE EL FINAL DEL CAMINO: sin eso, esta rama
     * se tragaria cualquier DELETE de mas abajo que llegara antes de la suya y
     * borraria una sucursal en vez de lo que se pidio. Es la trampa del §13 con
     * el alta de producto, que estuvo tapada años.
     */
    if (!s3 && method === 'DELETE') {
      return out(await rpc('delete_store', { p_store_id: storeId }));
    }
    // Que se vende en esa sucursal. Va en su propia ruta y no como un campo mas
    // de `settings` porque no es un dato de la ficha: reordena el catalogo
    // entero y la base lo rechaza si dejaria productos huerfanos de medida.
    // `profile`, el nombre que manda la PANTALLA.
    if (s3 === 'unit-profile' && !s4 && method === 'PATCH') {
      return out(
        await rpc('set_store_unit_profile', {
          p_store_id: storeId,
          p_profile: body.profile ?? null,
        }),
      );
    }

    // --- entradas por aprobar ---------------------------------------------
    /*
     * Se leen SOLO por RPC. Las dos tablas no tienen `select` para
     * `authenticated`, asi que aqui no hay ninguna consulta a PostgREST que
     * valga: `prev_cost_cents` decide el margen y quien lo captura no puede
     * verlo. Ver la migracion 20260813170000.
     */
    if (s3 === 'intakes' && s4 === 'queue') {
      return out(await rpc('get_intake_queue', { p_store_id: storeId }));
    }
    if (s3 === 'intakes' && s4 === 'mine') {
      return out(await rpc('get_my_intakes', { p_store_id: storeId, p_limit: Number(q.limit ?? 20) }));
    }
    if (s3 === 'intakes' && s4 === 'approve' && method === 'POST') {
      return out(
        await rpc('approve_stock_intakes', {
          p_store_id: storeId,
          p_intake_ids: body.intakeIds,
        }),
      );
    }
    if (s3 === 'intakes' && s4 && s5 === 'reject' && method === 'POST') {
      return out(
        await rpc('reject_stock_intake', {
          p_store_id: storeId,
          p_intake_id: Number(s4),
          p_reason: body.reason,
        }),
      );
    }
    if (s3 === 'intakes' && s4 && s5 === 'cancel' && method === 'POST') {
      return out(
        await rpc('cancel_stock_intake', { p_store_id: storeId, p_intake_id: Number(s4) }),
      );
    }
    if (s3 === 'intakes' && method === 'POST') {
      return out(
        // Los nombres de la izquierda son los del argumento SQL; los de la
        // derecha, los que manda la PANTALLA. Copiar `p_lines` en el cuerpo deja
        // el valor en undefined sin que TypeScript diga nada (§13).
        await rpc('submit_stock_intake', {
          p_store_id: storeId,
          p_lines: (body.lines as Row[]).map((l) => ({
            product_id: l.productId ?? null,
            qty_milli: l.qtyMilli,
            unit_cost_cents: l.unitCostCents,
            new_sku: l.newSku ?? null,
            new_name: l.newName ?? null,
            new_unit: l.newUnit ?? null,
            new_is_weighed: l.newIsWeighed ?? null,
            new_pack_size: l.newPackSize ?? null,
            new_price_cents: l.newPriceCents ?? null,
            new_barcode: l.newBarcode ?? null,
          })),
          p_idempotency_key: body.idempotencyKey,
          p_supplier_id: body.supplierId ?? null,
          p_reference: body.reference ?? null,
          p_instructed_by: body.instructedBy ?? null,
          p_note: body.note ?? null,
        }),
      );
    }

    // --- proveedores ------------------------------------------------------
    /*
      EL `!s4` NO SOBRA: sin el, esta rama se traga TODO POST bajo `purchases`,
      incluido `/purchases/:id/cancel`, que se declara mas abajo y por lo tanto
      nunca se alcanzaba. El efecto era silencioso y caro: cancelar una entrada
      llamaba a `create_purchase` con `body.items` en `undefined`, o sea que
      reventaba en `.map()` antes de tocar la red y la pantalla enseñaba un error
      generico. El boton de deshacer del acuse —la unica puerta que habia para
      corregir una entrada— llevaba asi desde que se escribio.

      Y ahi esta la explicacion de la medicion que motivo el historial: 14
      entradas registradas y CERO canceladas. No era solo que faltara donde;
      es que el unico donde que habia estaba roto.

      Se arregla en la CONDICION y no moviendo el bloque de sitio, porque el
      orden es una propiedad fragil: la siguiente ruta que alguien escriba
      encima vuelve a taparlo sin que nada se queje.
    */
    if (s3 === 'purchases' && !s4 && method === 'POST') {
      return out(
        await rpc('create_purchase', {
          p_store_id: storeId,
          p_items: (body.items as Row[]).map((i) => ({
            product_id: i.productId,
            qty_milli: i.qtyMilli,
            unit_cost_cents: i.unitCostCents,
          })),
          p_idempotency_key: body.idempotencyKey,
          p_supplier_id: body.supplierId ?? null,
          p_reference: body.reference ?? null,
          p_note: body.note ?? null,
        }),
      );
    }
    if (s3 === 'purchases' && s4 && s5 === 'cancel' && method === 'POST') {
      return out(
        await rpc('cancel_purchase', {
          p_store_id: storeId,
          p_purchase_id: Number(s4),
          p_reason: body.reason,
        }),
      );
    }
    if (s3 === 'purchases') {
      /*
        La lista de seleccion vive en constantes y no aqui: no cabe en la ventana
        con la que `npm run audit:reads` la encuentra. El porque completo esta en
        `PURCHASE_HISTORY_EMBEDS`.

        LOS RENGLONES VIAJAN CON LA LISTA, y eso es una decision, no descuido.
        Pedirlos aparte al abrir cada entrada seria una consulta por toque en la
        pantalla donde alguien esta BUSCANDO cual de las ultimas entradas trae el
        error: se abren cinco antes de dar con ella. Aqui caben porque una nota
        de proveedor trae unos pocos renglones y la ventana es corta.

        `purchase_items` y `products` SI se pueden incrustar, al reves que
        `sale_items`: conservan el privilegio de SELECT de tabla. Lo que esconde
        el costo de compra no es un candado de columna sino la politica RLS, que
        exige `purchase:manage` — un cajero recibe cero filas, no un 403.
      */
      const rows = await unwrap<Row[]>(
        supabase
          .from('purchases')
          .select(`${PURCHASE_COLUMNS}, ${PURCHASE_HISTORY_EMBEDS}`)
          .eq('store_id', storeId)
          .order('created_at', { ascending: false })
          .limit(Number(q.limit ?? 50)),
      );

      /*
       * QUE ENTRADA BLOQUEA A CUAL: el `PURCHASE_NOT_LATEST` de la base,
       * anticipado aqui para no ofrecer un boton cuyo unico desenlace sea el
       * rechazo (§10, el alta que pregunta de quien es un codigo al recibirlo).
       *
       * La base rechaza cancelar una entrada si ALGUNO de sus productos tiene
       * una entrada viva posterior, porque entonces el `prev_cost_cents`
       * guardado ya no es el costo al que habria que volver.
       *
       * El recorrido va de la MAS NUEVA hacia atras, asi que al mirar una
       * entrada ya se vieron todas las posteriores: basta recordar, por
       * producto, el folio vivo mas reciente. Y el resultado es exacto dentro de
       * la ventana —no una aproximacion— porque cualquier compra posterior a una
       * de la lista es, por ser posterior, tambien de la lista.
       */
      const lastLiveFolio = new Map<number, number>();
      const superseded = new Map<number, number>();
      for (const r of rows) {
        const items = (r.purchase_items ?? []) as Row[];
        if (r.status === 'cancelled') continue;
        let blocker: number | null = null;
        for (const line of items) {
          const seen = lastLiveFolio.get(Number(line.product_id));
          if (seen !== undefined && (blocker === null || seen < blocker)) blocker = seen;
        }
        if (blocker !== null) superseded.set(Number(r.id), blocker);
        for (const line of items) lastLiveFolio.set(Number(line.product_id), Number(r.folio));
      }

      return out({
        purchases: rows.map((r) => {
          const items = (r.purchase_items ?? []) as Row[];
          return {
            id: r.id,
            folio: r.folio,
            supplierId: r.supplier_id,
            supplierName: r.suppliers?.name ?? null,
            userName: r.buyer?.full_name ?? null,
            reference: r.reference,
            totalCents: r.total_cents,
            note: r.note,
            businessDate: r.business_date,
            createdAt: r.created_at,
            status: r.status ?? 'completed',
            cancelledAt: r.cancelled_at ?? null,
            cancelReason: r.cancel_reason ?? null,
            cancelledByName: r.canceller?.full_name ?? null,
            supersededByFolio: superseded.get(Number(r.id)) ?? null,
            lines: items.map((line) => ({
              productId: line.product_id,
              // El nombre de HOY: si se capturo mal y se corrigio, aqui se ve el
              // corregido. Es lo contrario del `name_snapshot` del ticket, y a
              // proposito — ver `purchaseHistoryLineSchema`.
              name: line.products?.name ?? '',
              sku: line.products?.sku ?? '',
              // `pz` y no 'pieza': la unidad es un enum (`UNITS`), no una etiqueta.
              // Un valor de fuera del enum no falla, se PINTA: la hoja acaba
              // diciendo "a $188.76 por undefined" al buscarlo en `UNIT_SPOKEN`.
              unit: line.products?.unit ?? 'pz',
              isWeighed: Boolean(line.products?.is_weighed),
              qtyMilli: line.qty_milli,
              unitCostCents: line.unit_cost_cents,
              lineTotalCents: line.line_total_cents,
            })),
          };
        }),
      });
    }
  }

  // --- proveedores (global) -------------------------------------------------
  if (seg[1] === 'suppliers') {
    /*
      Corregir un proveedor, y apagarlo con `isActive: false` en vez de
      borrarlo: `purchases.supplier_id` apunta aqui y borrar dejaria entradas
      historicas sin decir de quien vino la mercancia.
    */
    if (seg[2] && method === 'PATCH') {
      return out({
        supplier: await rpc('update_supplier', {
          p_store_id: Number(q.storeId ?? 0),
          p_supplier_id: Number(seg[2]),
          p_name: body.name,
          p_contact: body.contact ?? null,
          p_phone: body.phone ?? null,
          p_lead_time_days: body.leadTimeDays ?? null,
          p_notes: body.notes ?? null,
          p_is_active: body.isActive ?? null,
        }),
      });
    }
    if (method === 'POST') {
      return out({
        supplier: await rpc('create_supplier', {
          p_store_id: Number(q.storeId ?? 0),
          p_name: body.name,
          p_contact: body.contact ?? null,
          p_phone: body.phone ?? null,
          p_lead_time_days: body.leadTimeDays ?? 3,
          p_notes: body.notes ?? null,
        }),
      });
    }
    const rows = await unwrap<Row[]>(
      supabase.from('suppliers').select('*').eq('is_active', true).order('name'),
    );
    return out({
      suppliers: rows.map((r) => ({
        id: r.id,
        name: r.name,
        contact: r.contact,
        phone: r.phone,
        leadTimeDays: r.lead_time_days,
        notes: r.notes,
        isActive: r.is_active,
        createdAt: r.created_at,
      })),
    });
  }

  throw new ApiError('NOT_FOUND', `Ruta no contemplada: ${method} ${path}`, 404);
}

/**
 * Clave de idempotencia para operaciones que mueven dinero.
 *
 * Si la caja pierde la respuesta —se cayo el WiFi justo al cobrar— el reintento
 * lleva la misma clave y la base devuelve la venta original en lugar de
 * cobrarle dos veces al cliente.
 */
export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID().replace(/-/g, '');
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}
